/* eslint-disable @typescript-eslint/no-explicit-any */
import { usePermissions } from "@/features/clients/hooks/use-permissions";
import { useTranslation } from "@/features/utils/i18n";
import {
  BellIcon,
  CodeIcon,
  GearIcon,
  ShieldIcon,
  SquaresFourIcon,
} from "@phosphor-icons/react";

export type CompanyPage = {
  title: string;
  url: string;
  icon: any;
};

/**
 * The pages grouped under "Entreprise": one entry in the main sidebar, with
 * this list unfolded under it, indented, while one of them is open. Declared
 * once so the entry and its sub-entries cannot disagree on what a member may
 * open — the entry links to the first page of this list, and is active on
 * every one of them.
 */
export function useCompanyPages(clientId: string): CompanyPage[] {
  const { t } = useTranslation("sidebar");
  const permissions = usePermissions();
  const has = (permission: string) => permissions.includes(permission as any);

  return [
    has("settings:general") && {
      title: t("menu.general"),
      url: `/${clientId}/settings/general`,
      icon: GearIcon,
    },
    // The users of the tenant — who can sign in, as opposed to who is on the
    // team: deliberately not next to "Équipe" under Ressources. Merging the two
    // is how a salon's team came to be a list of logins.
    has("settings:members") && {
      title: t("menu.users"),
      url: `/${clientId}/settings/users`,
      icon: ShieldIcon,
    },
    // A company setting, not a personal one: this page says what the team is
    // notified about, how often, and who receives it — so it sits behind the
    // same permission as the rest of the company settings.
    has("settings:general") && {
      title: t("menu.notifications"),
      url: `/${clientId}/settings/notifications`,
      icon: BellIcon,
    },
    has("settings:accounts") && {
      title: t("menu.externalAccounts"),
      url: `/${clientId}/settings/accounts`,
      icon: SquaresFourIcon,
    },
    // The public API: its credentials are the account's own API keys, so there
    // is nothing to configure here — but an integrator with no page to land on
    // has no way of discovering the API exists at all.
    has("calls:create") && {
      title: t("menu.api"),
      url: `/${clientId}/settings/api`,
      icon: CodeIcon,
    },
  ].filter(Boolean) as CompanyPage[];
}
